import { type BaseEntity } from "@/shared/types";
import { z } from "zod";
import type { BarcodeSize } from "@entities/part";

// ---------------------------------------------------------------------------
// Asset Category
// ---------------------------------------------------------------------------

export interface AssetCategory extends BaseEntity {
  accountId: string;
  name: string;
  description?: string;
  sortingOrder?: number;
}

export const assetCategoryFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "common:validation.nameRequired")
    .max(100, "common:validation.nameMaxLength100"),
  description: z
    .string()
    .max(500, "common:validation.descriptionMax500")
    .optional()
    .or(z.literal("")),
  sortingOrder: z.coerce.number().optional().nullable(),
});

export type AssetCategoryFormData = z.infer<typeof assetCategoryFormSchema>;

// ---------------------------------------------------------------------------
// Asset Model
// ---------------------------------------------------------------------------

export type AssetModelStatus = "ACTIVE" | "ARCHIVED";

export interface AssetModelCount {
  problemCategoryAssetModels?: number | null;
  maintenanceTemplates?: number | null;
  inspectionTemplates?: number | null;
  compatibleParts?: number | null;
  assets?: number | null;
  documents?: number | null;
}

export interface AssetModel extends BaseEntity {
  accountId: string;
  assetCategoryId: string;
  assetCategory?: AssetCategory;
  name: string;
  modelNumber?: string;
  description?: string;
  manufacturer?: string;
  notes?: string;
  status?: AssetModelStatus;
  count?: AssetModelCount;
  imageUrl?: string;
  // Technical specifications (standard fields)
  voltage?: number;
  powerKw?: number;
  frequencyHz?: number;
  current?: number;
  protection?: string;
  weight?: number;
  dimensions?: string;
  energyEfficiencyClass?: string;
  /** Controls which standard fields are shown. */
  standardFieldsVisibility?: StandardFieldsVisibility | null;
  /** GraphQL: at least one active maintenance template on this model. */
  hasActiveTemplates?: boolean | null;
}

export interface StandardFieldsVisibility {
  voltage?: boolean;
  powerKw?: boolean;
  frequencyHz?: boolean;
  current?: boolean;
  protection?: boolean;
  weight?: boolean;
  dimensions?: boolean;
  energyEfficiencyClass?: boolean;
}

export const STANDARD_FIELDS_VISIBILITY_KEYS = [
  "voltage",
  "powerKw",
  "frequencyHz",
  "current",
  "protection",
  "weight",
  "dimensions",
  "energyEfficiencyClass",
] as const;

export const assetModelFormSchema = z.object({
  assetCategoryId: z.string().min(1, "common:validation.categoryRequired"),
  name: z
    .string()
    .trim()
    .min(1, "common:validation.nameRequired")
    .max(200, "common:validation.nameMaxLength200"),
  modelNumber: z.string().max(100).optional().or(z.literal("")),
  description: z
    .string()
    .max(500, "common:validation.descriptionMax500")
    .optional()
    .or(z.literal("")),
  manufacturer: z
    .string()
    .min(1, "common:validation.manufacturerRequired")
    .max(150, "common:validation.manufacturerMax150"),
});

export type AssetModelFormData = z.infer<typeof assetModelFormSchema>;

// Custom fields (model-specific data & asset fields structure)
export type AssetModelCustomFieldType = "Text" | "Number" | "Date";
export type AssetModelCustomFieldDataAssignment = "model_level" | "per_asset";

export interface AssetModelCustomField {
  id: string;
  fieldLabel: string;
  fieldType: AssetModelCustomFieldType;
  groupName: string;
  dataAssignment: AssetModelCustomFieldDataAssignment;
  value?: string;
  isRequired?: boolean;
  helperText?: string;
  defaultValue?: string;
  sortOrder: number;
}

export interface CreateCustomFieldBody {
  fieldLabel: string;
  fieldType: AssetModelCustomFieldType;
  groupName: string;
  dataAssignment: AssetModelCustomFieldDataAssignment;
  /** Include `null` on PATCH to clear a previously set value */
  value?: string | null;
  isRequired?: boolean;
  helperText?: string;
  /** Include `null` on PATCH to clear a previously set default */
  defaultValue?: string | null;
  sortOrder?: number;
}

// ---------------------------------------------------------------------------
// Asset
// ---------------------------------------------------------------------------

export interface AssetHoliday {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  type?: string;
  year?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface LocationRef {
  id: string;
  name: string;
  categoryId?: string | null;
  accountId?: string | null;
  street?: string | null;
  zip?: string | null;
  city?: string | null;
  country?: string | null;
  phone?: string | null;
}

export interface AssetLabelRef {
  id: string;
  name: string;
  color?: string | null;
}

export interface AssetLabel {
  id: string;
  asset_id: string;
  label_id: string;
  createdAt: Date;
  updatedAt?: Date;
}

/** Lifecycle status from REST list (e.g. model assets tab) */
export type AssetLifecycleStatus = "DRAFT" | "ACTIVE" | "INACTIVE";

export interface Asset extends BaseEntity {
  accountId: string | null;
  name: string;
  description?: string | null;
  serial_number?: string | null;
  asset_number?: string | null;
  commissioning_date?: string | null;
  asset_model_id?: string | null;
  asset_model?: AssetModel | null;
  location_id?: string | null;
  location?: LocationRef | null;
  /** Set when returned from REST asset list (e.g. GET /assets?modelIds=) */
  status?: AssetLifecycleStatus | null;
  labels?: AssetLabelRef[] | null;
  holidays?: AssetHoliday[];
  canFollowUpAppointmentsSaturdays?: boolean;
  canFollowUpAppointmentsSundays?: boolean;
  /** Count of planned (open) maintenances for this asset (from backend). */
  plannedMaintenancesCount?: number | null;
  /** Count of planned (open) inspections for this asset (from backend). */
  plannedInspectionsCount?: number | null;
  /** Count of documents linked to this asset (from backend). */
  documentsCount?: number | null;
  /** Open tickets (not RESOLVED/CLOSED) — GraphQL asset detail. */
  openTicketsCount?: number | null;
  /** Parts linked to this asset — GraphQL asset detail. */
  linkedPartsCount?: number | null;
  /** Total operating hours (used for maintenance interval modes). */
  operating_hours?: number | null;
}

export const assetFormSchema = z.object({
  name: z.string().trim().min(1).max(500),
  description: z.string().max(2000).optional().or(z.literal("")),
  serial_number: z.string().max(200).optional().or(z.literal("")),
  asset_number: z.string().max(200).optional().or(z.literal("")),
  commissioning_date: z.string().optional().nullable(),
  asset_model_id: z.string().min(1),
  location_id: z.string().min(1),
  label_ids: z.array(z.string()).optional().default([]),
});

/** Copy-from-asset flow: POST with saveAsDraft true — location/commissioning optional on backend. */
export const assetFormSchemaDuplicate = assetFormSchema.omit({ location_id: true }).extend({
  location_id: z.string(),
});

export type AssetFormData = z.infer<typeof assetFormSchema>;

export interface CreateAssetPayload {
  name: string;
  description?: string;
  serial_number?: string;
  asset_number?: string;
  commissioning_date?: string | null;
  asset_model_id: string;
  saveAsDraft: boolean;
  location_id?: string;
}

export type UpdateAssetPayload = Partial<CreateAssetPayload>;

// ---------------------------------------------------------------------------
// Asset Label Configuration
// ---------------------------------------------------------------------------

export type AssetLabelElementType = "qr" | "text" | "barcode";

export type AssetLabelPaperFormat = "A4" | "62x69" | "76x25" | "76x52";

export type AssetLabelQRSize = "small" | "standard" | "large";

export type AssetLabelTextType = "id" | "name" | "assetNumber";

export interface AssetLabelElementPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface AssetLabelElementBase {
  id: string;
  type: AssetLabelElementType;
  position: AssetLabelElementPosition;
  visible: boolean;
}

export interface AssetLabelQRCodeElement extends AssetLabelElementBase {
  type: "qr";
  size: AssetLabelQRSize;
}

export interface AssetLabelTextElement extends AssetLabelElementBase {
  type: "text";
  textType: AssetLabelTextType;
  fontSize: number;
  fontWeight: "normal" | "bold";
}

export interface AssetLabelBarcodeElement extends AssetLabelElementBase {
  type: "barcode";
  size: BarcodeSize;
}

export type AnyAssetLabelElement =
  | AssetLabelQRCodeElement
  | AssetLabelTextElement
  | AssetLabelBarcodeElement;

export interface AssetLabelPaperFormatSpec {
  name: string;
  widthMm: number;
  heightMm: number;
  widthPx: number;
  heightPx: number;
}

export const ASSET_LABEL_PAPER_FORMATS: Record<AssetLabelPaperFormat, AssetLabelPaperFormatSpec> = {
  A4: {
    name: "A4",
    widthMm: 210,
    heightMm: 297,
    widthPx: 794,
    heightPx: 1123,
  },
  "62x69": {
    name: "62 × 69 mm",
    widthMm: 62,
    heightMm: 69,
    widthPx: 234,
    heightPx: 261,
  },
  "76x25": {
    name: "76 × 25 mm",
    widthMm: 76,
    heightMm: 25,
    widthPx: 287,
    heightPx: 94,
  },
  "76x52": {
    name: "76 × 52 mm",
    widthMm: 76,
    heightMm: 52,
    widthPx: 287,
    heightPx: 196,
  },
};

export const ASSET_LABEL_QR_SIZES_PX: Record<AssetLabelQRSize, number> = {
  small: 80,
  standard: 120,
  large: 150,
};

export interface AssetLabelConfiguration {
  paperFormat: AssetLabelPaperFormat;
  qrSize: AssetLabelQRSize;
  barcodeSize: BarcodeSize;
  elements: AnyAssetLabelElement[];
}

export const DEFAULT_ASSET_LABEL_CONFIG: AssetLabelConfiguration = {
  paperFormat: "A4",
  qrSize: "standard",
  barcodeSize: "standard",
  elements: [
    {
      id: "qr-1",
      type: "qr",
      size: "standard",
      position: { x: 50, y: 50, width: 120, height: 120 },
      visible: true,
    },
    {
      id: "text-name",
      type: "text",
      textType: "name",
      fontSize: 18,
      fontWeight: "bold",
      position: { x: 190, y: 50, width: 300, height: 30 },
      visible: true,
    },
    {
      id: "text-assetNumber",
      type: "text",
      textType: "assetNumber",
      fontSize: 14,
      fontWeight: "normal",
      position: { x: 190, y: 90, width: 300, height: 25 },
      visible: true,
    },
    {
      id: "text-id",
      type: "text",
      textType: "id",
      fontSize: 12,
      fontWeight: "normal",
      position: { x: 190, y: 125, width: 300, height: 20 },
      visible: true,
    },
  ],
};

// ---------------------------------------------------------------------------
// Asset Model Maintenance Template
// ---------------------------------------------------------------------------

export type AssetModelMaintenanceIntervalMode = "fixed_date" | "relative" | "operating_hours";

export type AssetModelMaintenanceIntervalType = "day" | "week" | "month" | "year";

export interface MaintenanceTemplateTaskInput {
  name: string;
  description?: string | null;
  type?: string | null;
  isMandatory?: boolean;
  targetValue?: number | null;
  referenceImages: string[];
  sortOrder?: number | null;
}

export interface AssetModelMaintenanceTemplateTask {
  id: string;
  name: string;
  description?: string | null;
  type?: string | null;
  isMandatory?: boolean | null;
  targetValue?: number | null;
  referenceImages?: string[] | null;
  /** @deprecated Prefer referenceImages; kept for older responses. */
  fileId?: string | null;
  sortOrder?: number | null;
}

export type AssetModelMaintenanceTemplateKind = "maintenance" | "inspection";

export interface AssetModelMaintenanceTemplate {
  id: string;
  title: string;
  description?: string | null;
  type?: AssetModelMaintenanceTemplateKind | null;
  isActive: boolean;
  intervalMode: AssetModelMaintenanceIntervalMode;
  intervalNumber: number | null;
  intervalType: AssetModelMaintenanceIntervalType | null;
  intervalOperatingHours: number | null;
  durationDays?: number | null;
  durationHours: number | null;
  durationMinutes: number | null;
  overdueDays?: number | null;
  upcomingDays?: number | null;
  affectedComponent?: string | null;
  specialHint?: string | null;
  isExternal?: boolean | null;
  tasks?: AssetModelMaintenanceTemplateTask[];
}

export interface CreateAssetModelMaintenanceTemplateBody {
  title: string;
  type: AssetModelMaintenanceTemplateKind;
  description?: string;
  isActive?: boolean;
  intervalMode: AssetModelMaintenanceIntervalMode;
  intervalNumber?: number | null;
  intervalType?: AssetModelMaintenanceIntervalType | null;
  intervalOperatingHours?: number | null;
  durationDays?: number | null;
  durationHours?: number | null;
  durationMinutes?: number | null;
  overdueDays?: number | null;
  upcomingDays?: number | null;
  affectedComponent?: string | null;
  specialHint?: string | null;
  isExternal?: boolean | null;
  tasks?: MaintenanceTemplateTaskInput[];
}

export type UpdateAssetModelMaintenanceTemplateBody =
  Partial<CreateAssetModelMaintenanceTemplateBody>;

// ---------------------------------------------------------------------------
// Asset Model Compatible Parts
// ---------------------------------------------------------------------------

export type CompatiblePartTag = "recommended" | "critical" | "wear_part" | "maintenance_standard";

export const COMPATIBLE_PART_TAG_VALUES: readonly CompatiblePartTag[] = [
  "recommended",
  "critical",
  "wear_part",
  "maintenance_standard",
] as const;

export interface CompatiblePartSummary {
  id: string;
  name: string;
  number: string;
}

export interface AssetModelCompatiblePart {
  id: string;
  partId: string;
  tags: CompatiblePartTag[];
  referenceQuantity: number;
  part: CompatiblePartSummary;
}

export interface BulkCompatiblePartItem {
  partId: string;
  tags: CompatiblePartTag[];
  referenceQuantity: number;
}

export interface BulkAssignCompatiblePartsBody {
  parts: BulkCompatiblePartItem[];
}

export interface PatchCompatiblePartBody {
  tags?: CompatiblePartTag[];
  referenceQuantity?: number;
}

// ---------------------------------------------------------------------------
// Asset Model Document
// ---------------------------------------------------------------------------

export interface AssetModelDocument {
  id: string;
  fileKey: string;
  fileName: string;
  mimeType: string;
  description?: string | null;
  createdAt: string;
}

export interface CreateAssetModelDocumentBody {
  fileKey: string;
  fileName: string;
  mimeType: string;
  description?: string;
}

export type AssetDocument = AssetModelDocument;

export type CreateAssetDocumentBody = CreateAssetModelDocumentBody;

// ---------------------------------------------------------------------------
// Asset Model Problem Categories
// ---------------------------------------------------------------------------

export interface AssetModelProblemCategoryAssignmentRow {
  id: string;
  problemCategoryId: string;
  assetModelId: string;
  problemCategory: { id: string; name: string };
}

export interface AssignAssetModelProblemCategoriesBody {
  problemCategoryIds: string[];
}

export interface UnassignAssetModelProblemCategoryResponse {
  unassigned: number;
}

// ---------------------------------------------------------------------------
// Document Library
// ---------------------------------------------------------------------------

export type DocumentLibrarySource = "asset" | "assetModel";

export interface DocumentLibraryItem {
  fileKey: string;
  fileName: string;
  mimeType: string;
  description: string | null;
  source: DocumentLibrarySource;
  sourceName: string;
  sourceId: string;
}

export interface DocumentLibraryMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface LinkDocumentFromLibraryBody {
  fileKey: string;
  fileName?: string;
  mimeType?: string;
  description?: string;
}

// ---------------------------------------------------------------------------
// Asset Field Values
// ---------------------------------------------------------------------------

export interface AssetFieldValueMerged {
  fieldId: string;
  fieldLabel: string;
  fieldType: string;
  groupName: string | null;
  helperText?: string | null;
  defaultValue?: string | null;
  isRequired: boolean;
  sortOrder: number;
  value: string | null;
}

export interface PutAssetFieldValuesBody {
  values: Array<{ customFieldId: string; value: string | null }>;
}
